<script setup lang="ts">
import type { UserProperties } from "@/APIClient";
import UserRoleBadge from "./UserRoleBadge.vue";
import DRUserAvatar from "../UI/DRUserAvatar.vue";
import UserState from "@/stores/UserState";

defineProps<{
  users: UserProperties[];
  showActions: boolean;
}>();

defineEmits<{
  (e: "toggle", userId: number): void;
  (e: "delete", userId: number): void;
}>();

const dateFormatter = (row: UserProperties) => {
  const date = new Date(row.joined_at);
  return date.toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};
</script>

<template>
  <div class="rounded overflow-hidden bordered">
    <el-table
      :data="users"
      style="width: 100%"
      :row-style="{ height: '65px' }"
      header-cell-class-name="fw-normal"
    >
      <el-table-column
        prop="full_name"
        :label="$t('dashboard:users.table.column.fullName')"
        min-width="120"
        show-overflow-tooltip
      >
        <template #default="scope">
          <div class="d-flex align-items-center gap-2">
            <DRUserAvatar :user="scope.row" :size="32" />
            <div>
              {{ scope.row.full_name }}
              <span class="text-grey">#{{ scope.row.id }}</span>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column
        prop="email"
        :label="$t('dashboard:users.table.column.email')"
        min-width="120"
        show-overflow-tooltip
      />
      <el-table-column
        prop="role"
        :label="$t('dashboard:users.table.column.role')"
      >
        <template #default="scope">
          <UserRoleBadge :role="scope.row.role" />
        </template>
      </el-table-column>
      <el-table-column
        prop="joined_at"
        :label="$t('dashboard:users.table.column.joinedAt')"
        :formatter="dateFormatter"
      />
      <el-table-column
        prop="id"
        :label="$t('dashboard:users.table.column.plansCount')"
      >
        <template #default="scope">
          <el-tag>{{ scope.row.drawingsCount }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column
        v-if="showActions"
        prop="id"
        :label="$t('dashboard:users.table.column.activated')"
        align="right"
      >
        <template #default="scope">
          <button
            v-if="UserState.data"
            @click.prevent.stop="$emit('toggle', scope.row.id)"
            :disabled="
              scope.row.role === 'SUPERADMIN' ||
              UserState.data?.id === scope.row.id
            "
            class="btn-flat"
          >
            <i v-if="scope.row.enabled" class="bi-toggle-on text-success"></i>
            <i v-else class="bi-toggle-off text-black-50"></i>
          </button>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<style lang="scss" scoped>
.btn-flat {
  padding: 8px;
  border-radius: 8px;
  border: none;
  background-color: transparent;
  font-size: 1.5rem;
}
</style>
